import { useEffect } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { useDeferUntilInteraction } from "@/lib/defer";

// Reputation review widget — embedded exactly as provided, allowed to expand
// to its full natural height (no forced heights, clipping, or scrollbars).
// The iframe + its resize script are heavy third-party resources, so we hold
// them until the first user interaction and reserve space to avoid layout shift.
const REVIEW_SCRIPT = "https://reputationhub.site/reputation/assets/review-widget.js";
const REVIEW_SRC =
  "https://reputationhub.site/reputation/widgets/review_widget/1fTgrNTA2ypYqephqcO9?widgetId=6a628c534369c01dd2cfa1f2";

const ReviewWidget = () => {
  const ready = useDeferUntilInteraction();

  useEffect(() => {
    if (!ready) return;
    if (!document.querySelector(`script[src="${REVIEW_SCRIPT}"]`)) {
      const s = document.createElement("script");
      s.src = REVIEW_SCRIPT;
      s.async = true;
      document.body.appendChild(s);
    }
  }, [ready]);

  return (
    <section className="section-padding bg-muted/30">
      <div className="container-max">
        <div className="text-center mb-10">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="eyebrow-pill mb-4"
          >
            <Star className="h-3 w-3 fill-current" /> Reviews
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl text-foreground mb-4 tracking-tight font-bold"
          >
            What Our <span className="text-gradient-primary">Neighbors</span> Say
          </motion.h2>
          <div className="flex items-center justify-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-5 w-5 fill-secondary text-secondary" />
            ))}
          </div>
        </div>

        <div className="max-w-6xl mx-auto" style={{ minHeight: 520 }}>
          {ready ? (
            <iframe
              className="lc_reviews_widget"
              src={REVIEW_SRC}
              title="Customer reviews for Summit Neighbor Fencing Co."
              frameBorder="0"
              scrolling="no"
              style={{ minWidth: "100%", width: "100%", minHeight: 520 }}
            />
          ) : (
            <div
              aria-hidden="true"
              className="flex items-center justify-center rounded-2xl border border-border/50 bg-card/40"
              style={{ minHeight: 520 }}
            >
              <span className="text-muted-foreground text-sm">Loading reviews…</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ReviewWidget;
